import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Loader2, CheckCircle2, Users, ArrowRight, AlertTriangle, Sparkles,
} from "lucide-react";

/**
 * Admin-only "Merge to Syncro customer" modal.
 *
 * Fetches top 5 fuzzy-matched Syncro customers for this card (reusing the
 * same matcher that runs on auto-create) and lets ops relink to the
 * correct one. If a Syncro ticket is already attached, the ticket is
 * reassigned to the new customer server-side so comments stay in-place
 * but future billing / emails land on the right account.
 */
export default function SyncroMergeModal({ client, onClose, onMerged }) {
  const [matches, setMatches] = useState(null);
  const [strategy, setStrategy] = useState("none");
  const [currentId, setCurrentId] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    try {
      const { data } = await api.get(
        `/commissions/customers/${client.id}/syncro/fuzzy-matches`);
      setMatches(data.matches || []);
      setStrategy(data.strategy || "none");
      setCurrentId(data.current_syncro_customer_id || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fuzzy match failed");
      setMatches([]);
    }
  }

  async function doMerge() {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/customers/${client.id}/syncro/merge-to-customer`,
        { syncro_customer_id: picked.id });
      if (data.reason === "already_linked") {
        toast.info("Already linked to this Syncro customer");
      } else {
        toast.success(
          `Merged into ${data.to_business_name || `#${picked.id}`}` +
          (data.ticket_moved ? ` — ticket #${client.syncro_ticket_number || ""} moved` : "")
        );
      }
      onMerged(data.client);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed");
    } finally { setBusy(false); setConfirm(false); }
  }

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="syncro-merge-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl border border-slate-800 bg-slate-900 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-slate-800 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
              Merge to Syncro customer
            </div>
            <div className="text-sm font-semibold text-slate-100 truncate">
              {client.business_name || client.contact_name || client.email || "(no name)"}
              {client.syncro_ticket_number && (
                <span className="ml-2 text-xs text-slate-500 font-mono">· ticket #{client.syncro_ticket_number}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-slate-800 text-slate-400"
            data-testid="syncro-merge-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <p className="text-xs text-slate-400 leading-relaxed">
            Top 5 Syncro customers that look like this card, matched by email, business
            name, phone, and contact name. Pick the correct one to merge into — if a
            Syncro ticket is attached it&apos;ll be reassigned so comments stay put
            but future billing lands on the right account.
          </p>

          {matches === null ? (
            <div className="text-slate-500 text-sm inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching Syncro…
            </div>
          ) : matches.length === 0 ? (
            <div className="text-slate-500 text-sm">
              No fuzzy matches found. Use <b>Find in Syncro</b> instead to search manually.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="syncro-merge-matches">
              {matches.map((m) => {
                const isPicked = picked?.id === m.id;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(m)}
                      disabled={m.is_current}
                      data-testid={`syncro-merge-pick-${m.id}`}
                      className={`w-full text-left rounded-lg border p-3 transition-colors ${
                        m.is_current
                          ? "border-slate-800 bg-slate-950/40 opacity-60 cursor-not-allowed"
                          : isPicked
                            ? "border-violet-500/60 bg-violet-500/10"
                            : "border-slate-800 bg-slate-950/40 hover:border-violet-500/40 hover:bg-slate-950"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-100 inline-flex items-center gap-2 flex-wrap">
                            <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            {m.business_name || m.fullname
                              || [m.firstname, m.lastname].filter(Boolean).join(" ")
                              || `#${m.id}`}
                            {m.is_best_match && (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                                <Sparkles className="w-3 h-3" /> Best match
                              </span>
                            )}
                            {m.is_current && (
                              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                                · currently linked
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 truncate">
                            {[m.email, m.phone, [m.city, m.state].filter(Boolean).join(" ")]
                              .filter(Boolean).join(" · ") || "—"}
                          </div>
                          <div className="text-[10px] font-mono text-slate-600 mt-1">
                            Syncro id {m.id}
                          </div>
                        </div>
                        {isPicked && (
                          <CheckCircle2 className="w-4 h-4 text-violet-300 shrink-0 mt-1" />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {strategy && strategy !== "none" && (
            <div className="text-[11px] text-slate-500">
              Auto-matcher strategy: <span className="text-slate-300 font-mono">{strategy}</span>
            </div>
          )}
        </div>

        <footer className="px-5 py-4 border-t border-slate-800 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => setConfirm(true)}
            disabled={!picked || busy}
            data-testid="syncro-merge-confirm-open"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowRight className="w-4 h-4" />
            Merge into Syncro #{picked?.id || "—"}
          </button>
        </footer>
      </div>

      {confirm && picked && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setConfirm(false)}
        >
          <div
            className="bg-slate-900 border border-violet-500/40 rounded-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="syncro-merge-confirm-modal"
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="font-display text-lg tracking-tight">Confirm merge</h3>
                <p className="text-sm text-slate-400 mt-1">
                  This card will be relinked to{" "}
                  <span className="text-violet-300 font-medium">
                    {picked.business_name || picked.fullname || `Syncro #${picked.id}`}
                  </span>.
                  {client.syncro_ticket_id && (
                    <>
                      {" "}The Syncro ticket{" "}
                      <span className="font-mono text-slate-300">
                        #{client.syncro_ticket_number || client.syncro_ticket_id}
                      </span>{" "}
                      will be reassigned to this customer.
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirm(false)}
                className="px-3 py-2 text-sm rounded-lg border border-slate-800 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doMerge}
                disabled={busy}
                data-testid="syncro-merge-confirm-btn"
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-semibold disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {busy ? "Merging…" : "Merge"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
